import { round } from "lodash";

import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { CareComponent } from "../Models/CareElements/CareComponent";
import { CareElement } from "../Models/CareElements/CareElement";
import { VitalSignCareElement } from "../Models/CareElements/VitalSignCareElement";
import { VitalSignCareElementName } from "../Models/CareElements/VitalSignCareElementName";
import { VitalSignExam } from "../Models/CareElements/VitalSignExam";
import { ICareComponentService } from "./ICareComponentService";
import { VitalSignCareElementGridHelper } from "./VitalSignCareElementGridHelper";

class VitalSignsCareComponentService implements ICareComponentService {
    public processCareComponent(careComponent: CareComponent, patientContactId: string, encounterDate: Date): void {
        if (!encounterDate) {
            console.error("Encounter date is not defined in VitalSignsCareComponentService");
            return;
        }

        const vitalSigns = VitalSignsCareComponentService.getVitalSigns(patientContactId, encounterDate);
        careComponent.CareElements = this.getVitalSignsCareElements(vitalSigns, careComponent.CareElements as VitalSignCareElement[]);
    }

    private static readonly vitalSignsData: Record<string, {
        valueProvider: (vitalSign: Partial<MedicalItemVitalSign>) => {
            value: number | null;
            value2: number | null;
            individualUnit: string | null;
        };
    }> = {
            [VitalSignCareElementName.BloodPressure]: {
                valueProvider: vitalSign =>
                    vitalSign.BPSys && vitalSign.BPDia
                        ? { value: round(vitalSign.BPSys), value2: round(vitalSign.BPDia), individualUnit: null }
                        : { value: null, value2: null, individualUnit: null }
            },
            [VitalSignCareElementName.BodyMassIndex]: {
                valueProvider: vitalSign =>
                    vitalSign.MassBodyIndex
                        ? { value: round(vitalSign.MassBodyIndex, 2), value2: null, individualUnit: null }
                        : { value: null, value2: null, individualUnit: null }
            },
            [VitalSignCareElementName.WaistCircumference]: {
                valueProvider: vitalSign =>
                    vitalSign.WaistCircumference
                        ? { value: round(vitalSign.WaistCircumference, 2), value2: null, individualUnit: cacheStore.GetLocalizedValueByLookupId(vitalSign.WaistCircumferenceUnitLookupId) }
                        : { value: null, value2: null, individualUnit: null }
            },
            [VitalSignCareElementName.Height]: {
                valueProvider: vitalSign =>
                    vitalSign.Height
                        ? { value: round(vitalSign.Height, 2), value2: null, individualUnit: cacheStore.GetLocalizedValueByLookupId(vitalSign.HeightUnitLookupId) }
                        : { value: null, value2: null, individualUnit: null }
            },
            [VitalSignCareElementName.Weight]: {
                valueProvider: vitalSign =>
                    vitalSign.Weight
                        ? { value: round(vitalSign.Weight, 2), value2: null, individualUnit: cacheStore.GetLocalizedValueByLookupId(vitalSign.WeightUnitLookupId) }
                        : { value: null, value2: null, individualUnit: null }
            },
            [VitalSignCareElementName.HeartRate]: {
                valueProvider: vitalSign =>
                    vitalSign.HeartRate
                        ? { value: round(vitalSign.HeartRate), value2: null, individualUnit: null }
                        : { value: null, value2: null, individualUnit: null }
            }
        };

    public static getVitalSigns(patientContactId: string, encounterDate: Date): MedicalItemVitalSign[] {
        const medicalSummary = patientStore.PatientMedicalSummaryModels[patientContactId];
        if (!medicalSummary)
            return [];

        const vitalSignsByEmrTemplateId = medicalSummary
            .get("MedicalItemsVitalSign")
            .filter(p =>
                new Date(p.PharmaStartDate).toDateOnly() <= new Date(encounterDate).toDateOnly() &&
                p.EMRTemplateId &&
                !p.EMRTemplateIsInvalid &&
                p.EMRTemplateMedicalItemType === MedicalItemTypeEnum.VitalSigns
            )
            .toArray()
            .toLookup(p => p.EMRTemplateId);

        const vitalSigns: MedicalItemVitalSign[] = Object.keys(vitalSignsByEmrTemplateId)
            .map(key =>
                MedicalItemModel.sortByCreationDateDesc(vitalSignsByEmrTemplateId[key]).firstOrDefault()
            )
            .filter(p => p != null);

        return vitalSigns.sortByProperty(p => p.PharmaStartDate);
    }

    private getVitalSignsCareElements(
        vitalSigns: Partial<MedicalItemVitalSign>[],
        vitalSignCareElements: VitalSignCareElement[]
    ): CareElement[] {
        if (!vitalSignCareElements?.length || !vitalSigns?.length)
            return vitalSignCareElements;

        vitalSignCareElements.forEach(vitalSignCareElement => {
            vitalSignCareElement.Exams = vitalSigns.map(vitalSign =>
                VitalSignsCareComponentService.createExamForVitalSign(
                    vitalSignCareElement.Name as VitalSignCareElementName,
                    vitalSign
                ));
        });

        // Filter out exams that are null in all care elements, to not display an empty column
        this.removeExamsWithAllNullValues(vitalSignCareElements);

        return vitalSignCareElements;
    }

    private removeExamsWithAllNullValues(vitalSignCareElements: VitalSignCareElement[]): void {
        const exams = vitalSignCareElements?.firstOrDefault()?.Exams;

        exams?.forEach(exam => {
            const isNullForAll = vitalSignCareElements.every(careElement =>
                careElement.Exams?.firstOrDefault(careElementExam => careElementExam.Date === exam.Date)?.Value == null
            );

            if (isNullForAll) {
                vitalSignCareElements.forEach(careElement => {
                    careElement.Exams = careElement.Exams.filter(careElementExam => careElementExam.Date !== exam.Date);
                });
            }
        });
    }

    public static addVitalSignToCareElements(
        vitalSign: MedicalItemVitalSign,
        vitalSignCareElements: VitalSignCareElement[],
        encounterDate: Date
    ): VitalSignCareElement[] {
        if (!vitalSign.PharmaStartDate) {
            return vitalSignCareElements;
        }

        const vitalSignDate = new Date(vitalSign.PharmaStartDate);
        const encounter = new Date(encounterDate);

        const isValidDate = vitalSignDate < encounter
            || VitalSignCareElementGridHelper.isSameDay(vitalSignDate, encounter);

        if (!isValidDate) {
            return vitalSignCareElements;
        }

        if (!this.hasAtLeastOneNonEmptyValue(vitalSign, vitalSignCareElements)) {
            return vitalSignCareElements;
        }

        return vitalSignCareElements.map(careElement => {
            const exam = this.createExamForVitalSign(careElement.Name as VitalSignCareElementName, vitalSign);

            return new VitalSignCareElement({
                ...careElement,
                Exams: [...(careElement.Exams ?? []), exam]
            });
        });
    }

    private static getUnitForVitalType(careElement: VitalSignCareElementName, vitalSign: MedicalItemVitalSign): string | null {
        switch (careElement) {
            case VitalSignCareElementName.WaistCircumference:
                return vitalSign.WaistCircumferenceUnitLookupId ? cacheStore.GetLocalizedValueByLookupId(vitalSign.WaistCircumferenceUnitLookupId) : null;
            case VitalSignCareElementName.Height:
                return vitalSign.HeightUnitLookupId ? cacheStore.GetLocalizedValueByLookupId(vitalSign.HeightUnitLookupId) : null;
            case VitalSignCareElementName.Weight:
                return vitalSign.WeightUnitLookupId ? cacheStore.GetLocalizedValueByLookupId(vitalSign.WeightUnitLookupId) : null;
            default:
                return null;
        }
    }

    private static createExamForVitalSign(
        careElement: VitalSignCareElementName,
        vitalSign: Partial<MedicalItemVitalSign>
    ): VitalSignExam {
        const data = this.vitalSignsData[careElement];
        const values = data?.valueProvider(vitalSign) ?? {
            value: null,
            value2: null,
            individualUnit: null
        };

        return new VitalSignExam({
            Value: values.value,
            Value2: values.value2,
            Unit: values.individualUnit ?? this.getUnitForVitalType(careElement, vitalSign as MedicalItemVitalSign),
            Date: vitalSign.PharmaStartDate ?? null
        });
    }

    private static hasAtLeastOneNonEmptyValue(
        vitalSign: MedicalItemVitalSign,
        careElements: VitalSignCareElement[]
    ): boolean {
        return careElements.some(careElement => {
            const vitalType = careElement.Name as VitalSignCareElementName;
            const data = VitalSignsCareComponentService.vitalSignsData[vitalType];

            if (!data)
                return false;

            const values = data.valueProvider(vitalSign);

            return values.value != null || values.value2 != null;
        });
    }
}

export { VitalSignsCareComponentService };
